import React, { useState, useEffect, useContext } from 'react';
import { SmokeThrowEvent } from './replayParser';
import { SmokeClipRequestDTO, requestSmokeClip, getSmokeClipStatus } from '../../../services/SmokeReplayService';
import { AuthContext } from '../../../context/AuthContext';

interface SmokeDetailPanelProps {
    smokeThrow: SmokeThrowEvent;
    playerName: string;
    team: 'T' | 'CT';
    throwerSteamId: string;
    throwTick: number; // Actual demo tick (resolved from replay frames), not PLSR frame index
    onClose: () => void;
    // For clip request
    gameId: string;
    matchId: string;
    season: number;
    roundNumber: number;
    mapName: string;
    existingClipRequest?: SmokeClipRequestDTO | null;
    onClipRequested?: (request: SmokeClipRequestDTO) => void;
}

const T_COLOR = '#de9b35';
const CT_COLOR = '#5d79ae';

const SmokeDetailPanel: React.FC<SmokeDetailPanelProps> = ({
    smokeThrow, playerName, team, throwerSteamId, throwTick, onClose,
    gameId, matchId, season, roundNumber, mapName,
    existingClipRequest, onClipRequested,
}) => {
    const { token } = useContext(AuthContext);
    const [clipRequest, setClipRequest] = useState<SmokeClipRequestDTO | null>(existingClipRequest ?? null);
    const [requesting, setRequesting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const teamColor = team === 'T' ? T_COLOR : CT_COLOR;

    // Poll clip status while pending — immediate check on open, then every 5s
    useEffect(() => {
        if (!clipRequest || clipRequest.clipStatus === 'completed' || clipRequest.clipStatus === 'failed') return;

        const checkStatus = async () => {
            try {
                const status = await getSmokeClipStatus(clipRequest.id);
                setClipRequest(prev => prev ? { ...prev, clipStatus: status.clipStatus as SmokeClipRequestDTO['clipStatus'], clipUrl: status.clipUrl, clipThumbnailUrl: status.clipThumbnailUrl } : prev);
            } catch { }
        };

        checkStatus(); // immediate check — don't wait 5s for first poll
        const interval = setInterval(checkStatus, 5000);
        return () => clearInterval(interval);
    }, [clipRequest?.id, clipRequest?.clipStatus]);

    const handleRequestClip = async () => {
        if (!token) return;
        setRequesting(true);
        setError(null);
        try {
            const result = await requestSmokeClip({
                gameId, matchId, season, roundNumber, mapName,
                throwerSteamId,
                throwTick,
                throwerSlot: smokeThrow.throwerSlot,
                smokeEntityId: null,
            }, token);
            const newRequest = {
                ...result,
                gameId, matchId, season, roundNumber, mapName,
                throwerSteamId,
                throwTick,
                throwerSlot: smokeThrow.throwerSlot,
                smokeEntityId: null,
                clipUrl: null,
                clipThumbnailUrl: null,
            } as SmokeClipRequestDTO;
            setClipRequest(newRequest);
            onClipRequested?.(newRequest);
        } catch {
            setError('Failed to request clip');
        } finally {
            setRequesting(false);
        }
    };

    const throwStrengthLabel = smokeThrow.throwStrength === 'Full' ? 'Left Click'
        : smokeThrow.throwStrength === 'Short' ? 'Right Click'
        : smokeThrow.throwStrength === 'Medium' ? 'Both (Middle)'
        : 'Unknown';

    return (
        <div style={{
            position: 'absolute',
            top: 10, right: 10,
            width: 320,
            background: '#1a1a1a',
            borderRadius: 8,
            border: `1px solid ${teamColor}44`,
            color: '#e0e0e0',
            fontSize: 13,
            zIndex: 100,
            boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
            overflow: 'hidden',
        }}>
            {/* Header */}
            <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '10px 14px',
                background: '#222',
                borderBottom: `2px solid ${teamColor}`,
            }}>
                <span style={{ fontWeight: 600, fontSize: 14 }}>Smoke Throw Detail</span>
                <button onClick={onClose} style={{
                    background: 'none', border: 'none', color: '#888', cursor: 'pointer',
                    fontSize: 18, lineHeight: 1, padding: 0,
                }}>×</button>
            </div>

            {/* Player name */}
            <div style={{ padding: '8px 14px', borderBottom: '1px solid #333' }}>
                <span style={{ color: teamColor, fontWeight: 600 }}>{playerName}</span>
            </div>

            {/* Movement info */}
            <div style={{
                display: 'grid', gridTemplateColumns: '1fr 1fr 1fr',
                padding: '10px 14px', gap: 8,
                borderBottom: '1px solid #333',
            }}>
                <div>
                    <div style={{ color: '#888', fontSize: 11, marginBottom: 2 }}>Crouched</div>
                    <div style={{ color: smokeThrow.isCrouched ? '#a78bfa' : '#666' }}>
                        {smokeThrow.isCrouched ? '✓' : '—'}
                    </div>
                </div>
                <div>
                    <div style={{ color: '#888', fontSize: 11, marginBottom: 2 }}>Jumping</div>
                    <div style={{ color: smokeThrow.isJumping ? '#a78bfa' : '#666' }}>
                        {smokeThrow.isJumping ? '✓' : '—'}
                    </div>
                </div>
                <div>
                    <div style={{ color: '#888', fontSize: 11, marginBottom: 2 }}>Movement Speed</div>
                    <div>{smokeThrow.movementState}</div>
                </div>
            </div>

            {/* Throw info */}
            <div style={{
                display: 'grid', gridTemplateColumns: '1fr 1fr 1fr',
                padding: '10px 14px', gap: 8,
                borderBottom: '1px solid #333',
            }}>
                <div>
                    <div style={{ color: '#888', fontSize: 11, marginBottom: 2 }}>Throw Strength</div>
                    <div>{throwStrengthLabel}</div>
                </div>
                <div>
                    <div style={{ color: '#888', fontSize: 11, marginBottom: 6 }}>Movement Keys</div>
                    <WASDDisplay
                        w={smokeThrow.isForward}
                        a={smokeThrow.isMoveLeft}
                        s={smokeThrow.isBack}
                        d={smokeThrow.isMoveRight}
                        activeColor={teamColor}
                    />
                </div>
                <div>
                    <div style={{ color: '#888', fontSize: 11, marginBottom: 2 }}>Type</div>
                    <div>Smoke</div>
                </div>
            </div>

            {/* Crosshair */}
            <div style={{ padding: '8px 14px', borderBottom: '1px solid #333', fontSize: 12 }}>
                <span style={{ color: '#888' }}>Crosshair: </span>
                <span>Yaw {smokeThrow.yaw.toFixed(1)}°  Pitch {smokeThrow.pitch.toFixed(1)}°</span>
            </div>

            {/* Clip section */}
            <div style={{ padding: '12px 14px' }}>
                {clipRequest?.clipStatus === 'completed' && clipRequest.clipUrl ? (
                    <video
                        src={clipRequest.clipUrl}
                        controls
                        playsInline
                        style={{ width: '100%', borderRadius: 4 }}
                    />
                ) : clipRequest ? (
                    <div style={{
                        textAlign: 'center', padding: '16px 0', color: '#888',
                    }}>
                        {clipRequest.clipStatus === 'failed' ? (
                            <>
                                <div style={{ marginBottom: 8 }}>Clip generation failed</div>
                                <button onClick={handleRequestClip} disabled={requesting} style={btnStyle(teamColor)}>
                                    {requesting ? 'Requesting...' : 'Retry'}
                                </button>
                            </>
                        ) : (
                            <div>
                                Clip {clipRequest.clipStatus === 'requested' ? 'requested' :
                                    clipRequest.clipStatus === 'queued' ? 'queued' :
                                    clipRequest.clipStatus === 'recording' ? 'recording...' :
                                    clipRequest.clipStatus}
                                <div style={{ marginTop: 4, fontSize: 11 }}>Polling for updates...</div>
                            </div>
                        )}
                    </div>
                ) : (
                    <button onClick={handleRequestClip} disabled={requesting || !token} style={btnStyle(teamColor)}>
                        {requesting ? 'Requesting...' : 'Request Smoke Replay'}
                    </button>
                )}
                {error && <div style={{ color: '#ef4444', fontSize: 12, marginTop: 6 }}>{error}</div>}
            </div>
        </div>
    );
};

function btnStyle(color: string): React.CSSProperties {
    return {
        width: '100%',
        padding: '8px 0',
        background: color,
        color: '#fff',
        border: 'none',
        borderRadius: 4,
        cursor: 'pointer',
        fontWeight: 600,
        fontSize: 13,
    };
}

const WASDDisplay: React.FC<{
    w: boolean; a: boolean; s: boolean; d: boolean;
    activeColor: string;
}> = ({ w, a, s, d, activeColor }) => {
    const keyStyle = (active: boolean): React.CSSProperties => ({
        width: 22, height: 22,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        borderRadius: 3,
        fontSize: 11, fontWeight: 700,
        background: active ? activeColor : '#333',
        color: active ? '#fff' : '#666',
        border: `1px solid ${active ? activeColor : '#444'}`,
    });

    return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <div style={keyStyle(w)}>W</div>
            <div style={{ display: 'flex', gap: 2 }}>
                <div style={keyStyle(a)}>A</div>
                <div style={keyStyle(s)}>S</div>
                <div style={keyStyle(d)}>D</div>
            </div>
        </div>
    );
};

export default SmokeDetailPanel;
